import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, PageHeading } from '@hp/ui';
import { useCan, useMe } from '../api/me';
import { ErrorAlert } from '../components/error-alert';
import { LoadMore } from '../components/ui/load-more';
import { Stat, StatStrip } from '../components/ui/stat';
import { GenerateLinksPanel } from '../features/contact-updates/generate-links-panel';
import { useSelfserviceOverview, useSelfSubmissions } from '../features/contact-updates/queries';
import { LinksTable } from '../features/contact-updates/links-table';
import { SubmissionsTable } from '../features/contact-updates/submissions-table';

export default function ContactUpdates() {
  const { t } = useTranslation(['contactUpdates', 'common']);
  const me = useMe();
  const canManage = useCan()('selfservice.manage');
  const pdDomain = me.data?.pipedriveCompanyDomain;
  const overview = useSelfserviceOverview(true);
  const submissions = useSelfSubmissions();

  const items = useMemo(() => submissions.data?.pages.flatMap((p) => p.items) ?? [], [submissions.data]);

  return (
    <div className="bo-sections">
      <PageHeading title={t('title')} description={t('description')} />

      <p className="bo-callout" role="note">
        <Icon name="alert" />
        <span>
          <Trans t={t} i18nKey="callout" values={{ version: 'v1-cs-DRAFT' }} components={{ strong: <strong /> }} />
        </span>
      </p>

      <StatStrip>
        <Stat label={t('stats.activeLinks')} value={overview.data?.linksActive} />
        <Stat label={t('stats.used')} value={overview.data?.linksUsed} />
        <Stat label={t('stats.dead')} value={overview.data?.linksDead} />
        <Stat label={t('stats.submissions')} value={overview.data?.submissions} />
        <Stat label={t('stats.peopleWithConsent')} value={overview.data?.personsWithAnyGrant} />
      </StatStrip>
      <ErrorAlert error={overview.error} />

      {canManage && <GenerateLinksPanel pdDomain={pdDomain} />}

      <LinksTable canManage={canManage} pdDomain={pdDomain} />

      <SubmissionsTable items={items} isLoading={submissions.isLoading} error={submissions.error} pdDomain={pdDomain} />

      {submissions.hasNextPage && (
        <div className="bo-row">
          <LoadMore
            hasNextPage={submissions.hasNextPage}
            isFetchingNextPage={submissions.isFetchingNextPage}
            onClick={() => void submissions.fetchNextPage()}
          />
        </div>
      )}
    </div>
  );
}
